import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MenuItem } from "@mui/material";
import { Badge } from "react-bootstrap";
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { getPackageColor, getPackageName, getPackagePrice, getPackageProfit, getPackageTitle } from "../../../utils/packages";
import StatusChip from "../../../components/subscriptions/StatusChip";
import QuickActionModal from "../../../components/subscriptions/QuickActionModal";
import "../../accounts/view/ViewAccountPage.css";
import { MUI_TABLE } from '../../../utils/muiTableConfig';
import { Money, Username } from '../../../utils/privacyMask';


export default function RenewalsPage() {
  const navigate = useNavigate();

  const [subscriptions, setSubscriptions] = useState([]);
  const [accountsMap, setAccountsMap]     = useState({});
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [quickAction, setQuickAction]     = useState(null); // { mode, subscription } | null

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const [subRes, accRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/subscription/expired`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/account`, { headers }),
      ]);
      setSubscriptions(subRes.data);
      const map = {};
      accRes.data.forEach(a => { map[a._id] = a.username; });
      setAccountsMap(map);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch subscriptions");
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = useMemo(() => [
    {
      header: 'Account', accessorKey: 'accountID',
      Cell: ({ cell }) => <span style={{ fontWeight: 600 }}><Username value={accountsMap[cell.getValue()] || cell.getValue()} /></span>,
    },
    {
      header: 'Package', accessorKey: 'package',
      Cell: ({ cell }) => {
        const v = cell.getValue();
        return <Badge bg="" style={{ background: getPackageColor(v), fontSize: '0.72rem' }}>{getPackageTitle(v)} – {getPackageName(v)}</Badge>;
      },
    },
    {
      header: 'Renewal €', accessorFn: r => getPackagePrice(r.package), size: 100,
      Cell: ({ cell }) => <span style={{ color: '#34d399' }}><Money value={cell.getValue()} /></span>,
    },
    {
      header: 'Renewal P.', accessorFn: r => getPackageProfit(r.package), size: 100,
      Cell: ({ cell }) => <span style={{ color: '#38bdf8' }}><Money value={cell.getValue()} /></span>,
    },
    {
      header: 'Created', accessorKey: 'createdDate', size: 100,
      Cell: ({ cell }) => new Date(Number(cell.getValue())).toLocaleDateString(),
    },
    {
      header: 'Overdue', accessorKey: 'expireDate', size: 110,
      Cell: ({ cell }) => {
        const days = Math.round((Date.now() - Number(cell.getValue())) / 86400000) - 1;
        return <Badge bg={days < 0 ? 'success' : 'danger'} style={{ fontSize: '0.72rem' }}>{Math.abs(days)}d {days < 0 ? 'left' : 'ago'}</Badge>;
      },
    },
    {
      header: 'Status', accessorKey: 'active', size: 110,
      Cell: ({ row }) => <StatusChip subscription={row.original} />,
    },
    {
      header: 'Notes', accessorKey: 'notes',
      Cell: ({ cell }) => <span style={{ color: '#64748b' }}>{cell.getValue() || '—'}</span>,
    },
  ], [accountsMap]);

  const table = useMaterialReactTable({
    columns,
    data: subscriptions ?? [],
    enableColumnFilterModes: true,
    enableColumnOrdering: true,
    enableGrouping: true,
    enableColumnPinning: true,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: false,
    enableGlobalFilter: true,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: false,
      columnPinning: { left: ['mrt-row-expand', 'mrt-row-select'], right: ['mrt-row-actions'] },
      sorting: [{ id: 'expireDate', desc: true }],
    },
    renderRowActionMenuItems: ({ row }) => [
      <MenuItem key="renew"  onClick={() => setQuickAction({ mode: 'renew', subscription: row.original })} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-arrow-repeat" style={{ fontSize: '0.9rem', color: '#06b6d4' }} />Renew
      </MenuItem>,
      <MenuItem key="extend" onClick={() => setQuickAction({ mode: 'extend', subscription: row.original })} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-calendar-plus" style={{ fontSize: '0.9rem', color: '#f59e0b' }} />Extend
      </MenuItem>,
      <MenuItem key="edit"   onClick={() => navigate(`/subscription/edit/${row.original._id}`)} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-pencil-square" style={{ fontSize: '0.9rem', color: '#64748b' }} />Edit
      </MenuItem>,
      <MenuItem key="view"   onClick={() => navigate(`/subscription/view/${row.original._id}`)} sx={{ m: 0, gap: 1 }}>
        <i className="bi bi-eye" style={{ fontSize: '0.9rem', color: '#64748b' }} />View
      </MenuItem>,
      <MenuItem key="delete" onClick={() => {}} sx={{ m: 0, gap: 1, color: '#f87171' }}>
        <i className="bi bi-trash" style={{ fontSize: '0.9rem', color: '#f87171' }} />Delete
      </MenuItem>,
    ],
    ...MUI_TABLE,
  });

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <Sidebar title="Awaiting Renewal" />

      <div className="view-page">
        {error && (
          <div style={{ padding: '12px 16px', marginBottom: 20, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', borderRadius: 10, color: '#f87171', fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
          </div>
        )}

        <div className="view-panel">
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-arrow-repeat" style={{ color: '#06b6d4' }} />
              Awaiting Renewal
            </h6>
            <span className="view-panel-count">{subscriptions.length}</span>
          </div>
          <MaterialReactTable table={table} />
        </div>
      </div>

      {quickAction && (
        <QuickActionModal
          mode={quickAction.mode}
          subscription={quickAction.subscription}
          onClose={() => setQuickAction(null)}
          onSuccess={fetchData}
        />
      )}
    </>
  );
}
